import { useCallback, useEffect, useState } from 'react';
import { api, ErroApi } from '../api';
import { numero } from '../formato';
import { useSessao } from '../sessao';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';

/**
 * O catálogo de itens de medição — e de onde sai a quantidade de cada um.
 *
 * ─── A decisão que esta tela existe para receber ─────────────────────
 *
 * A lista de preços da Testesolo tem 75 itens; o catálogo da bancada tem
 * 55 métodos. 52 casam pelo código exato e o sistema já apura sozinho.
 * Os outros 23 são de dois tipos, e os dois são decisão da casa:
 *
 *   · `TRI3.CIU`, `CD4.NAT` — itens que EMPACOTAM vários corpos de prova
 *     num preço só. Medir isso exige saber se a unidade contratada é o
 *     ensaio (com três CP dentro) ou o corpo de prova;
 *   · `ADNS`, `ADNS.CD`, `ADNS.CS` (até a 0160, `ADEN.*`) — os mesmos ensaios que a bancada
 *     chama de `ADNS`. É divergência de nomenclatura entre a lista de
 *     preços e o laboratório, e casá-los é um «de acordo», não uma
 *     dedução.
 *
 * O §25 proíbe o sistema inventar regra de medição. Então ele não
 * inventa: ele pergunta aqui, mostra o palpite ao lado, e espera.
 *
 * ─── Por que uma tela e não um e-mail ────────────────────────────────
 *
 * São 23 decisões de uma frase cada. Por e-mail elas viram um parágrafo
 * que alguém transcreve para o código; aqui elas viram linha de tabela,
 * com autor, data e trilha — e a apuração inteira obedece no instante
 * seguinte, sem versão nova do sistema.
 */

interface Regra {
  metodo_id: string; metodo: string; metodo_nome: string;
  fonte: string; fator: string; observacao: string | null;
}
interface Sugestao { metodo_id: string; metodo: string; metodo_nome: string }

interface Item {
  id: string;
  codigo: string;
  descricao: string;
  categoria: string | null;
  unidade: string;
  unidade_nome: string;
  status: string;
  apura: boolean;
  regras: Regra[];
  sugestoes: Sugestao[];
}

export function ItensDeMedicao() {
  const { pode } = useSessao();
  const [busca, setBusca] = useState('');
  const [soSemRegra, setSoSemRegra] = useState(false);
  const [lista, setLista] = useState<Item[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ligando, setLigando] = useState<{ item: Item; sug?: Sugestao } | null>(null);

  const carregar = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (busca.trim()) q.set('busca', busca.trim());
      if (soSemRegra) q.set('so_sem_regra', '1');
      const r = await api<Item[]>(`/itens-medicao${q.toString() ? `?${q}` : ''}`);
      setLista(r); setErro(null);
    } catch (e) { setErro(e); }
  }, [busca, soSemRegra]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, busca ? 250 : 0);
    return () => clearTimeout(t);
  }, [carregar, busca]);

  async function desligar(item: Item, r: Regra) {
    setErro(null);
    try {
      await api(`/itens-medicao/${item.id}/metodos/${r.metodo_id}`,
        { metodo: 'DELETE' });
      await carregar();
    } catch (e) { setErro(e); }
  }

  const semRegra = (lista ?? []).filter((i) => !i.apura).length;
  const podeMexer = pode('medicao.ajustar');

  return (
    <div>
      <div className="linha-topo">
        <h1 className="cresce">Itens de medição</h1>
      </div>
      <p className="sub">
        A lista de preços da Testesolo, e de qual ensaio sai a quantidade
        de cada item. O que estiver ligado a um método o sistema apura
        sozinho, a partir dos ensaios aprovados.
      </p>

      {erro ? <ErroDaApi erro={erro} /> : null}

      <div className="linha" style={{ marginBottom: '.9rem' }}>
        <input type="search" value={busca} className="cresce"
          placeholder="Código ou descrição — ex.: TRI, adensamento, CBR"
          onChange={(e) => setBusca(e.target.value)} />
        <label className="caixa">
          <input type="checkbox" checked={soSemRegra}
            onChange={(e) => setSoSemRegra(e.target.checked)} />
          Só os que o sistema não sabe apurar
        </label>
      </div>

      {lista === null ? <Carregando texto="Carregando o catálogo" /> : (
        <>
          {semRegra > 0 && !soSemRegra && (
            <Aviso tipo="atencao"
              titulo={`${semRegra} item(ns) o sistema não sabe apurar.`}>
              Eles entram no escopo e contam como previsto, mas o executado
              precisa ser lançado à mão até alguém dizer de qual ensaio sai
              a quantidade. O sistema não adivinha por semelhança de
              código: um item medido errado é pior que um item que ele
              declara não saber medir (§25).
            </Aviso>
          )}

          {lista.length === 0 ? (
            <Vazio>Nenhum item encontrado com esta busca.</Vazio>
          ) : (
            <div className="rola">
              <table className="dados">
                <thead>
                  <tr>
                    <th>Código</th><th>Serviço</th><th>Unidade</th>
                    <th>De onde sai a quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((i) => (
                    <tr key={i.id}>
                      <td className="mono">{i.codigo}</td>
                      <td>
                        {i.descricao}
                        {i.categoria && (
                          <div className="sub">{i.categoria}</div>
                        )}
                      </td>
                      <td>{i.unidade_nome}</td>
                      <td>
                        {i.regras.length === 0 ? (
                          <div className="linha">
                            <Selo tom="atencao">Apuração manual</Selo>
                            {podeMexer && i.sugestoes.length > 0 && (
                              <span className="sub">
                                parece{' '}
                                {i.sugestoes.slice(0, 3).map((s) => (
                                  <button key={s.metodo_id} type="button"
                                    className="botao discreto pequeno"
                                    title={s.metodo_nome}
                                    onClick={() => setLigando({ item: i, sug: s })}>
                                    {s.metodo}
                                  </button>
                                ))}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="regras">
                            {i.regras.map((r) => (
                              <span className="regra" key={r.metodo_id}>
                                <span className="mono" title={r.metodo_nome}>
                                  {r.metodo}
                                </span>
                                <span className="sub">
                                  por {r.fonte === 'ensaio'
                                    ? 'ensaio' : 'corpo de prova'}
                                  {Number(r.fator) !== 1
                                    && ` · ×${numero(r.fator, 4, true)}`}
                                </span>
                                {podeMexer && (
                                  <button type="button"
                                    className="botao discreto pequeno"
                                    onClick={() => void desligar(i, r)}>
                                    tirar
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {podeMexer && (
                          <button type="button"
                            className="botao discreto pequeno"
                            onClick={() => setLigando({ item: i })}>
                            + ligar a um método
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {ligando && (
        <DialogoRegra item={ligando.item} sugerido={ligando.sug}
          aoFechar={() => setLigando(null)}
          aoPronto={() => { setLigando(null); void carregar(); }} />
      )}
    </div>
  );
}

/**
 * De qual método sai a quantidade, e quanto cada ocorrência vale.
 *
 * O `fator` é o número que responde à pergunta dos pacotes: se um ensaio
 * de três corpos de prova vale um item inteiro, o fator é 1; se cada
 * corpo de prova vale um terço do item, é 0,3333. O diálogo mostra o que
 * cada escolha vai significar na prática, com um exemplo — porque é
 * exatamente aqui que uma decisão certa vira um número errado.
 */
function DialogoRegra(
  { item, sugerido, aoFechar, aoPronto }:
  {
    item: Item; sugerido?: Sugestao;
    aoFechar(): void; aoPronto(): void;
  },
) {
  const [metodos, setMetodos] = useState<Sugestao[] | null>(null);
  const [metodoId, setMetodoId] = useState(sugerido?.metodo_id ?? '');
  const [fonte, setFonte] = useState<'ensaio' | 'corpo_de_prova'>('ensaio');
  const [fator, setFator] = useState('1');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const erroApi = erro instanceof ErroApi ? erro : null;

  useEffect(() => {
    api<Array<{ id: string; codigo: string; nome: string }>>('/normas/metodos')
      .then((r) => setMetodos(r.map((m) => ({
        metodo_id: m.id, metodo: m.codigo, metodo_nome: m.nome,
      }))))
      .catch(() => setMetodos([]));
  }, []);

  const n = Number(fator.replace(',', '.'));
  const pronto = !!metodoId && Number.isFinite(n) && n > 0;

  async function enviar() {
    setErro(null); setEnviando(true);
    try {
      await api(`/itens-medicao/${item.id}/metodos`, {
        metodo: 'POST',
        corpo: { metodoId, fonte, fator: n },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto titulo="De onde sai a quantidade" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub">
        <span className="mono">{item.codigo}</span> — {item.descricao}
        <br />Unidade contratada: <b>{item.unidade_nome}</b>
      </p>

      <div className="campo">
        <span className="rotulo">Método de ensaio</span>
        <select value={metodoId} onChange={(e) => setMetodoId(e.target.value)}>
          <option value="">— escolha —</option>
          {(metodos ?? []).map((m) => (
            <option key={m.metodo_id} value={m.metodo_id}>
              {m.metodo} — {m.metodo_nome}
            </option>
          ))}
        </select>
        {sugerido && (
          <span className="dica">
            Sugerido: <b>{sugerido.metodo}</b> — {sugerido.metodo_nome}. É
            palpite do código, não regra: confira antes de gravar.
          </span>
        )}
      </div>

      <div className="campo" role="group" aria-label="O que se conta">
        <span className="rotulo">O que se conta</span>
        <div className="escolha">
          <button type="button" aria-pressed={fonte === 'ensaio'}
            onClick={() => setFonte('ensaio')}>Cada ensaio</button>
          <button type="button" aria-pressed={fonte === 'corpo_de_prova'}
            onClick={() => setFonte('corpo_de_prova')}>
            Cada corpo de prova
          </button>
        </div>
        {fonte === 'corpo_de_prova' && (
          <Aviso tipo="atencao" titulo="Ainda não há de onde contar">
            O corpo de prova ainda não é uma entidade lançada no sistema —
            só o adensamento e o módulo de resiliência têm CP, e cada um
            do seu jeito. Escolher isto grava a decisão e a apuração vai
            passar a valer quando a entidade existir; até lá o item conta
            zero, e é melhor deixá-lo como apuração manual.
          </Aviso>
        )}
      </div>

      <Campo rotulo="Quanto cada um vale do item" inputMode="decimal"
        value={fator} onChange={(e) => setFator(e.target.value)}
        erro={erroApi?.campo('fator')}
        dica={fonte === 'ensaio'
          ? '1 = cada ensaio aprovado vale um item inteiro. Para um item '
            + 'que empacota três ensaios, use 0,3333.'
          : '1 = cada corpo de prova vale um item inteiro.'} />

      {Number.isFinite(n) && n > 0 && (
        <p className="dica">
          Na prática: três {fonte === 'ensaio' ? 'ensaios aprovados'
            : 'corpos de prova'} vão apurar{' '}
          <b>{numero(3 * n, 4, true)}</b>{' '}
          {item.unidade_nome.toLowerCase()} deste item.
        </p>
      )}

      <div className="rodape-dialogo">
        <button type="button" className="botao" disabled={!pronto || enviando}
          onClick={() => void enviar()}>
          {enviando ? 'Gravando…' : 'Gravar a regra'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}
          disabled={enviando}>Cancelar</button>
      </div>
    </Dialogo>
  );
}
